import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Header from '../components/Header';
import './SetsPage.css';
import { LoadingContext } from '../App';

const SetsPage = () => {
    const [seriesMap, setSeriesMap] = useState({});
    const [expandedSeries, setExpandedSeries] = useState({});
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const { setLoading } = useContext(LoadingContext);

    useEffect(() => {
        const fetchSets = async () => {
            setLoading(true);
            try {
                const response = await fetch(`${apiBaseUrl}/sets`);
                if (!response.ok) throw new Error(`Network error: ${response.statusText}`);
                const data = await response.json();

                // Group sets by series
                const grouped = data.reduce((acc, set) => {
                    if (!acc[set.series]) acc[set.series] = [];
                    acc[set.series].push(set);
                    return acc;
                }, {});

                // Build final map of { series: { headerSet, sets } }
                const filteredSeries = Object.entries(grouped).reduce((acc, [series, sets]) => {
                    if (['pop', 'other', 'np'].some(bad => series.toLowerCase().includes(bad))) return acc;

                    const filtered = sets.filter(s =>
                        !s.name.toLowerCase().includes('promos') &&
                        !s.name.toLowerCase().includes('energies') &&
                        !s.name.toLowerCase().includes('starter')
                    );
                    const validSets = filtered.length > 0 ? filtered : sets;
                    const headerSet = validSets.slice().sort((a, b) => new Date(a.release_date) - new Date(b.release_date))[0];
                    const fullList = sets.slice().sort((a, b) => new Date(b.release_date) - new Date(a.release_date));
                    acc[series] = { headerSet, fullList };
                    return acc;
                }, {});

                setSeriesMap(filteredSeries);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchSets();
    }, []);

    const toggleSeries = (series) => {
        setExpandedSeries(prev => ({
            ...prev,
            [series]: !prev[series]
        }));
    };

    const handleSetClick = (setName) => {
        const slug = setName.toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
        navigate(`/sets/${slug}`);
    };

    if (error) return <p>Error: {error}</p>;

    const sortedSeries = Object.values(seriesMap)
        .sort((a, b) => new Date(b.headerSet.release_date) - new Date(a.headerSet.release_date));

    return (
        <Layout>
            <div className="sets-page">
                <div className="sets-container">
                    {sortedSeries.map(({ headerSet, fullList }) => (
                        <div key={headerSet.series}>
                            <div 
                                className="set-item series-header"
                                onClick={() => toggleSeries(headerSet.series)}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="set-logo-container">
                                    <img src={headerSet.logo_image} alt={headerSet.name} className="set-logo" />
                                </div>
                                <div className="set-info">
                                    <div className="set-series-row">
                                        <strong className="series-name">{headerSet.series}</strong>
                                        <strong className="series-years">
                                            ({Math.min(...fullList.map(s => new Date(s.release_date).getFullYear()))}–{Math.max(...fullList.map(s => new Date(s.release_date).getFullYear()))})
                                        </strong>
                                    </div>
                                </div>
                                <div className="dropdown-indicator">
                                    ▼
                                </div>
                            </div>
                            {expandedSeries[headerSet.series] && (
                                <div className="sets-list">
                                    {fullList.map(set => (
                                        <div
                                            key={set.id}
                                            className="set-item"
                                            onClick={() => handleSetClick(set.name)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div className="set-logo-container">
                                                <img src={set.logo_image} alt={set.name} className="set-logo" />
                                            </div>
                                            <div className="set-info">
                                                <div className="set-name">
                                                    {set.name}
                                                    <span style={{ marginLeft: '10px', fontWeight: 'normal', color: "#555" }}>
                                                        ({new Date(set.release_date).getFullYear()})
                                                    </span>
                                                </div>
                                                <div className="set-details">
                                                    <span className="set-release">Released: {new Date(set.release_date).toLocaleDateString()}</span>
                                                    <span className="set-cards">Cards: {set.total}</span>
                                                    {set.ptcgo_code && <span className="set-code">PTCGO: {set.ptcgo_code}</span>}
                                                </div>
                                            </div>
                                            <div className="set-symbol-container">
                                                <img src={set.symbol_image} alt={`${set.name} symbol`} className="set-symbol" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </Layout>
    );
};

export default SetsPage;
